import { MailOutline as MailOutlineIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { signOut } from 'firebase/auth';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/components/icons';
import { refreshEmailVerification, resendEmailVerification } from '@/firebase/account-actions';
import { requiresEmailVerification } from '@/firebase/auth-policy';
import { useAuth, useUser } from '@/firebase/provider';

const RESEND_COOLDOWN_SECONDS = 30;

type VerificationAction = 'checking' | 'resending' | 'signingOut' | null;
type Feedback = { severity: 'error' | 'info' | 'success'; message: string } | null;

export function EmailVerificationGate({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const { user } = useUser();
  const [action, setAction] = useState<VerificationAction>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [, setAuthRevision] = useState(0);
  const isRefreshingRef = useRef(false);

  const needsVerification = user ? requiresEmailVerification(user) : false;

  useEffect(() => {
    setAction(null);
    setFeedback(null);
    setResendCooldown(0);
  }, [user?.uid]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const intervalId = window.setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendCooldown]);

  const refreshVerification = useCallback(async (showNotVerifiedFeedback: boolean) => {
    if (!user || isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    if (showNotVerifiedFeedback) setAction('checking');

    try {
      const isVerified = await refreshEmailVerification(user);
      setAuthRevision((revision) => revision + 1);

      if (!isVerified && showNotVerifiedFeedback) {
        setFeedback({ severity: 'info', message: t('emailVerification.notVerifiedYet') });
      }
    } catch {
      if (showNotVerifiedFeedback) {
        setFeedback({ severity: 'error', message: t('emailVerification.checkError') });
      }
    } finally {
      isRefreshingRef.current = false;
      if (showNotVerifiedFeedback) setAction(null);
    }
  }, [t, user]);

  useEffect(() => {
    if (!needsVerification) return;

    const handleWindowFocus = () => {
      void refreshVerification(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshVerification(false);
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [needsVerification, refreshVerification]);

  if (!user || !needsVerification) {
    return <>{children}</>;
  }

  const isBusy = action !== null;

  const handleResend = async () => {
    setAction('resending');
    setFeedback(null);
    auth.languageCode = i18n.resolvedLanguage?.split('-')[0] ?? null;

    try {
      await resendEmailVerification(user);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setFeedback({ severity: 'success', message: t('emailVerification.resendSuccess') });
    } catch {
      setFeedback({ severity: 'error', message: t('emailVerification.sendError') });
    } finally {
      setAction(null);
    }
  };

  const handleSignOut = async () => {
    setAction('signingOut');
    setFeedback(null);

    try {
      await signOut(auth);
    } catch {
      setFeedback({ severity: 'error', message: t('emailVerification.signOutError') });
      setAction(null);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}
    >
      <Stack spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: 440 }}>
        <Icons.logo className="w-16 h-16 text-primary" />
        <Card sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>
          <CardContent sx={{ px: { xs: 3, sm: 5 }, pt: 5, pb: 2, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <MailOutlineIcon fontSize="large" />
            </Box>
            <Typography component="h1" variant="h4" fontWeight={700} gutterBottom>
              {t('emailVerification.title')}
            </Typography>
            <Typography color="text.secondary">
              {t('emailVerification.description', { email: user.email ?? t('emailVerification.yourEmail') })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('emailVerification.hint')}
            </Typography>
            {feedback && (
              <Alert severity={feedback.severity} sx={{ mt: 3, textAlign: 'left' }}>
                {feedback.message}
              </Alert>
            )}
          </CardContent>
          <CardActions sx={{ px: { xs: 3, sm: 5 }, pb: 5, pt: 2, flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={isBusy}
              onClick={() => void refreshVerification(true)}
              startIcon={action === 'checking' ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {t('emailVerification.check')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={isBusy || resendCooldown > 0}
              onClick={() => void handleResend()}
              startIcon={action === 'resending' ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {resendCooldown > 0
                ? t('emailVerification.resendIn', { seconds: resendCooldown })
                : t('emailVerification.resend')}
            </Button>
            <Button
              variant="text"
              fullWidth
              disabled={isBusy}
              onClick={() => void handleSignOut()}
              startIcon={action === 'signingOut' ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {t('emailVerification.useDifferentAccount')}
            </Button>
          </CardActions>
        </Card>
      </Stack>
    </Container>
  );
}
